import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import { GetCollectionsNavigationQueryResult, useGetCollectionsNavigationQuery } from 'types/graphql';
import { useHeader } from 'src/dispensary/core-menu/components/header/use-header';
import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';
import { useActiveScheduledItems } from 'src/dispensary/hooks/use-active-scheduled-items';

type UseNavigationReturn = {
  isCategoriesOpen: boolean;
  handleCategoryClick: (label: string) => void;
  handleCategoriesClick: () => void;
  handleCollectionClick: (label: string) => void;
  closeCategories: () => void;
  collections: NonNullable<GetCollectionsNavigationQueryResult['data']>['getCollectionsNavigation'];
  trackEvent: (label: string) => void;
  hideDiscount: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  bottomWatcherRef: React.RefObject<HTMLDivElement>;
  hasOverflow: boolean;
  showGradient: boolean;
};

export function useNavigation(dispensary: { id: string; timezone?: string }): UseNavigationReturn {
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const { trackEvent } = useHeader();
  const hideDiscount = useHideDiscountFromConsumer(dispensary);
  const router = useRouter();

  const containerRef = useRef<HTMLDivElement>(null);
  const bottomWatcherRef = useRef<HTMLDivElement>(null);

  const bottomWatcher = useIntersectionObserver(bottomWatcherRef, {
    root: containerRef.current,
    threshold: 0.1,
    rootMargin: '0px 0px -10px 0px',
  });

  const { data } = useGetCollectionsNavigationQuery({
    skip: !dispensary,
    variables: {
      dispensaryId: dispensary.id,
      customCategory: false,
      enabled: true,
      activeOnly: true,
    },
  });

  const collections = useActiveScheduledItems({
    items: data?.getCollectionsNavigation ?? [],
    getSchedule: (collection) => collection.schedule,
    dispensaryTimezone: dispensary.timezone,
    enablePolling: true,
  });

  const closeCategories = useCallback((): void => {
    setIsCategoriesOpen(false);
  }, []);

  useEffect(() => {
    const checkOverflow = (): void => {
      const container = containerRef.current;
      if (container) {
        const hasScroll = container.scrollHeight > container.clientHeight;
        setHasOverflow(hasScroll);
      }
    };

    if (containerRef.current) {
      checkOverflow();
    }

    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [isCategoriesOpen]);

  useEffect(() => {
    const handleRouteChange = (): void => {
      closeCategories();
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, closeCategories]);

  const handleCategoriesClick = (): void => {
    trackEvent('Categories');
    setIsCategoriesOpen(!isCategoriesOpen);
  };

  const handleCategoryClick = (label: string): void => {
    trackEvent(`categories > ${label}`);
  };

  const handleCollectionClick = (label: string): void => {
    trackEvent(`collections > ${label}`);
    setIsCategoriesOpen(!isCategoriesOpen);
  };

  const showGradient = hasOverflow && !bottomWatcher?.isIntersecting;

  return {
    isCategoriesOpen,
    handleCategoryClick,
    handleCategoriesClick,
    handleCollectionClick,
    closeCategories,
    collections,
    trackEvent,
    hideDiscount,
    containerRef,
    bottomWatcherRef,
    hasOverflow,
    showGradient,
  };
}
